import { useEffect, useRef, useState } from 'react'
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clipboard,
  History,
  Loader2,
  MessageSquare,
  Mic,
  Pause,
  Play,
  RefreshCw,
  Send,
  Square,
  Target,
  X,
  Zap,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { AiCreditUsageLink } from '@/components/shared/AiCreditUsageLink'
import { CounterOfferDialog } from '@/components/deals/negotiation/CounterOfferDialog'
import {
  NegotiationEventConfirmationDialog,
} from '@/components/deals/negotiation/NegotiationEventConfirmationDialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import {
  createDealNote,
  generateDealNegotiationGuidance,
  createDealOffer,
  createDealVoiceNote,
  getDealNegotiationGuidance,
  getDealNegotiationGuidanceGeneration,
  getDealOffers,
  getDealNotes,
  updateDealNote,
  updateDealApi,
} from '@/lib/api'
import { looksPersistedDealId } from '@/lib/persisted-deal'
import { useSession } from '@/lib/session-context'
import { cn } from '@/lib/utils'

const UNAVAILABLE_AMOUNT = 'Unavailable'
const NO_COUNTER_RECEIVED = 'No counter received'

const CONFIRMATION_CONTENT = {
  vendor_rejected: {
    title: 'Confirm vendor rejection',
    description: 'Only confirm when the agent or vendor has explicitly rejected the current offer.',
    confirmLabel: 'Confirm Rejection',
  },
  creative_openness: {
    title: 'Confirm creative terms are open',
    description: 'Confirm the vendor or agent has indicated openness to a lease option, vendor finance, deferred payment, or another creative structure.',
    confirmLabel: 'Confirm Creative Openness',
  },
  follow_up_scheduled: {
    title: 'Confirm follow-up scheduled',
    description: 'Confirm a follow-up has been arranged with the agent or vendor.',
    confirmLabel: 'Confirm Follow-up',
  },
}
const SUGGESTED_EVENT_DIALOGS = {
  counter_received: { amountDialogType: 'counter' },
  terms_agreed: { amountDialogType: 'agreement' },
  initial_offer_submitted: { amountDialogType: 'initial_offer' },
  vendor_rejected: { confirmationType: 'vendor_rejected' },
  vendor_open_to_creative_terms: { confirmationType: 'creative_openness' },
  follow_up_scheduled: { confirmationType: 'follow_up_scheduled' },
}
const AUDIO_MIME_TYPES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/ogg;codecs=opus',
]
const TRANSCRIPTION_UNSUPPORTED_MESSAGE = 'Transcription is not supported in this browser.'
const LOSS_REASON_OPTIONS = [
  'Deal Rejected',
  'Sold to someone else',
  'Withdrawn from market',
]
const DEFAULT_LOSS_REASON = LOSS_REASON_OPTIONS[0]
const ACTIVE_GUIDANCE_STATUSES = new Set(['queued', 'running'])

function normalizeOutcomeStatus(status) {
  if (status === 'Agreed' || status === 'Won') return 'agreed'
  if (status === 'Lost') return 'lost'

  return 'negotiating'
}

function formatNegotiationStatus(status) {
  if (status === 'Won') return 'Won'
  if (status === 'Awaiting Response') return 'Awaiting Response'
  if (status === 'Countering') return 'Countering'
  if (status === 'Rejected') return 'Rejected'
  if (status === 'Follow Up') return 'Follow Up'
  if (status === 'Agreed') return 'Agreed'
  if (status === 'Lost') return 'Lost'

  return 'Awaiting Response'
}

function amountCentsFromCurrency(value) {
  const digits = String(value ?? '').replace(/[^\d.]/g, '')
  if (!digits) return null

  const numericValue = Number(digits)
  if (!Number.isFinite(numericValue)) return null

  return Math.round(numericValue * 100)
}

function currencyFromCents(value) {
  const pounds = Math.round(Number(value) / 100)

  return `£${new Intl.NumberFormat('en-GB').format(pounds)}`
}

function latestCounterAmount(offers, negotiationStatus) {
  const persistedCounter = offers.find((offer) => (
    offer.status === 'countered' &&
    ['counter', 'received'].includes(offer.direction) &&
    offer.amount
  ))
  if (persistedCounter) return persistedCounter.amount

  const summaryIsCounter = (
    negotiationStatus?.latestOfferStatus === 'countered' &&
    ['counter', 'received'].includes(negotiationStatus?.latestOfferDirection)
  )

  return summaryIsCounter ? negotiationStatus.latestOffer : null
}

function latestOutboundAmount(offers, negotiationStatus, initialOffer) {
  const persistedOutbound = offers.find((offer) => offer.direction === 'outbound' && offer.amount)
  if (persistedOutbound) return persistedOutbound.amount

  return negotiationStatus?.latestOfferDirection === 'outbound'
    ? negotiationStatus.latestOffer
    : initialOffer
}

function formatOfferDate(value) {
  if (!value) return 'Date not captured'

  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value))
}

function titleForOffer(offer) {
  if (offer.status === 'accepted') return 'Offer Accepted'
  if (offer.status === 'rejected') return 'Offer Rejected'
  if (offer.status === 'withdrawn') return 'Offer Withdrawn'
  if (offer.status === 'countered') return 'Counteroffer Received'
  if (offer.status === 'submitted') return 'Initial Offer Submitted'

  return 'Offer Drafted'
}

function iconForOffer(offer) {
  if (offer.status === 'accepted') return CheckCircle2
  if (offer.status === 'rejected' || offer.status === 'withdrawn') return X
  if (offer.status === 'countered') return History

  return Send
}

function toneForOffer(offer) {
  if (offer.status === 'accepted' || offer.status === 'submitted') return 'primary'
  if (offer.status === 'rejected' || offer.status === 'withdrawn') return 'neutral'
  if (offer.status === 'countered') return 'muted'

  return 'neutral'
}

function preferredAudioMimeType() {
  if (!window.MediaRecorder?.isTypeSupported) return undefined

  return AUDIO_MIME_TYPES.find((type) => window.MediaRecorder.isTypeSupported(type))
}

function formatDuration(milliseconds) {
  const totalSeconds = Math.max(1, Math.round(milliseconds / 1000))
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60

  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

function formatPlaybackTime(seconds) {
  if (!Number.isFinite(seconds)) return '0:00'

  const totalSeconds = Math.max(0, Math.round(seconds))
  const minutes = Math.floor(totalSeconds / 60)
  const remainingSeconds = totalSeconds % 60

  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
}

function progressWidthClass(progress) {
  if (progress >= 95) return 'w-full'
  if (progress >= 85) return 'w-11/12'
  if (progress >= 75) return 'w-10/12'
  if (progress >= 65) return 'w-8/12'
  if (progress >= 55) return 'w-7/12'
  if (progress >= 45) return 'w-6/12'
  if (progress >= 35) return 'w-5/12'
  if (progress >= 25) return 'w-4/12'
  if (progress >= 15) return 'w-3/12'
  if (progress >= 5) return 'w-1/12'

  return 'w-0'
}

function buildVoiceFile(blob) {
  return new File([blob], `negotiation-voice-note-${Date.now()}.webm`, {
    type: blob.type || 'audio/webm',
  })
}

function speechRecognitionConstructor() {
  return window.SpeechRecognition || window.webkitSpeechRecognition
}

function transcriptFromResult(event) {
  let finalTranscript = ''
  let interimTranscript = ''

  for (let index = event.resultIndex; index < event.results.length; index += 1) {
    const result = event.results[index]
    const transcript = result[0]?.transcript?.trim()
    if (!transcript) continue

    if (result.isFinal) {
      finalTranscript = `${finalTranscript} ${transcript}`.trim()
    } else {
      interimTranscript = `${interimTranscript} ${transcript}`.trim()
    }
  }

  return { finalTranscript, interimTranscript }
}

function noteEntry(note) {
  const content = note.content || note.body
  const placeholderTranscript = note.audioAttached && content === 'Voice note recorded.'

  return {
    id: note.id,
    type: note.type || note.categoryLabel || 'Manual Note',
    date: note.date || 'Just now',
    duration: note.duration,
    content: placeholderTranscript ? '' : content,
    audioUrl: note.audioUrl,
    audioAttached: note.audioAttached,
    createdAt: note.createdAt,
    metadata: note.metadata ?? {},
  }
}

function noteTimelineEntry(note) {
  return {
    id: `note-${note.id}`,
    title: note.type === 'Voice Note' ? 'Voice Note Logged' : 'Negotiation Note',
    date: note.createdAt,
    dateLabel: note.date,
    notes: note.content,
    audioUrl: note.audioUrl,
    duration: note.duration,
    icon: note.type === 'Voice Note' ? Mic : MessageSquare,
    tone: note.type === 'Voice Note' ? 'muted' : 'neutral',
  }
}

function offerTimelineEntry(offer) {
  return {
    id: `offer-${offer.id}`,
    title: titleForOffer(offer),
    date: offer.submittedAt ?? offer.createdAt,
    amount: offer.amount,
    notes: offer.notes,
    icon: iconForOffer(offer),
    tone: toneForOffer(offer),
  }
}

function guidanceActive(guidance) {
  return guidance && ACTIVE_GUIDANCE_STATUSES.has(guidance.status)
}

function sleep(milliseconds) {
  return new Promise((resolve) => {
    setTimeout(resolve, milliseconds)
  })
}

async function pollNegotiationGuidance({
  dealId,
  csrfToken,
  startedGuidance,
  requestId,
  latestRequestRef,
}) {
  let guidance = startedGuidance
  let result = { guidance }

  while (guidanceActive(guidance)) {
    await sleep(guidance.pollAfterMs ?? 2_000)
    if (latestRequestRef.current !== requestId) return null

    const payload = await getDealNegotiationGuidanceGeneration(
      dealId,
      guidance.id,
      { csrfToken },
    )
    if (latestRequestRef.current !== requestId) return null

    result = payload
    guidance = result.guidance
  }

  return result
}

export function DealNegotiationTab({
  deal,
  negotiationStatus,
  onLogUpdate,
  onLogOutcome,
  onNegotiationSaved,
  onReviewConcept,
}) {
  const { currentUser, csrfToken } = useSession()
  const shouldPersist = Boolean(currentUser && looksPersistedDealId(deal?.id))
  const initialOffer = deal?.appraisalSummary?.initialOffer ?? UNAVAILABLE_AMOUNT
  const targetClose = deal?.appraisalSummary?.targetClose ?? UNAVAILABLE_AMOUNT
  const [offers, setOffers] = useState([])
  const [notes, setNotes] = useState([])
  const latestPersistedOffer = offers[0]?.amount
  const latestRecordedOffer = negotiationStatus?.latestOffer ?? latestPersistedOffer ?? initialOffer
  const latestCounter = latestCounterAmount(offers, negotiationStatus)
  const latestOutboundOffer = latestOutboundAmount(offers, negotiationStatus, initialOffer)
  const walkAway = negotiationStatus?.walkAway ?? deal?.appraisalSummary?.maxPrice ?? UNAVAILABLE_AMOUNT
  const currentStatus = negotiationStatus?.status ?? 'Action Required'
  const statusLabel = formatNegotiationStatus(currentStatus)
  const normalizedOutcomeState = normalizeOutcomeStatus(negotiationStatus?.status)
  const [selectedOutcomeState, setSelectedOutcomeState] = useState(null)
  const [isOutcomeExpanded, setIsOutcomeExpanded] = useState(true)
  const [updateText, setUpdateText] = useState('')
  const [error, setError] = useState(null)
  const [isSavingOffer, setIsSavingOffer] = useState(false)
  const [isSavingNote, setIsSavingNote] = useState(false)
  const [isRecording, setIsRecording] = useState(false)
  const [isSavingVoice, setIsSavingVoice] = useState(false)
  const [amountDialogType, setAmountDialogType] = useState(null)
  const [counterAmount, setCounterAmount] = useState('')
  const [counterNotes, setCounterNotes] = useState('')
  const [counterError, setCounterError] = useState(null)
  const [pendingConfirmation, setPendingConfirmation] = useState(null)
  const [transcriptPreview, setTranscriptPreview] = useState('')
  const [transcriptionStatus, setTranscriptionStatus] = useState(null)
  const [guidance, setGuidance] = useState(null)
  const [guidanceError, setGuidanceError] = useState(null)
  const [isGeneratingGuidance, setIsGeneratingGuidance] = useState(false)
  const outcomeState = selectedOutcomeState ?? normalizedOutcomeState
  const recorderRef = useRef(null)
  const recognitionRef = useRef(null)
  const streamRef = useRef(null)
  const audioChunksRef = useRef([])
  const recordingStartedAtRef = useRef(null)
  const finalTranscriptRef = useRef('')
  const interimTranscriptRef = useRef('')
  const localAudioUrlsRef = useRef([])
  const latestGuidanceRequestRef = useRef(0)
  const onNegotiationSavedRef = useRef(onNegotiationSaved)

  useEffect(() => {
    onNegotiationSavedRef.current = onNegotiationSaved
  }, [onNegotiationSaved])

  useEffect(() => {
    if (!shouldPersist) return undefined

    let ignore = false

    Promise.all([
      getDealOffers(deal.id, { csrfToken }),
      getDealNotes(deal.id, { csrfToken }),
      getDealNegotiationGuidance(deal.id, { csrfToken }),
    ])
      .then(([offersPayload, notesPayload, guidancePayload]) => {
        if (ignore) return
        setOffers(offersPayload.offers ?? [])
        setNotes((notesPayload.notes ?? [])
          .filter((note) => ['Voice Note', 'Manual Note'].includes(note.type))
          .map(noteEntry))
        setGuidance(guidancePayload.guidance ?? null)

        if (guidanceActive(guidancePayload.guidance)) {
          const requestId = latestGuidanceRequestRef.current + 1
          latestGuidanceRequestRef.current = requestId
          setIsGeneratingGuidance(true)
          pollNegotiationGuidance({
            dealId: deal.id,
            csrfToken,
            startedGuidance: guidancePayload.guidance,
            requestId,
            latestRequestRef: latestGuidanceRequestRef,
          })
            .then((resolvedPayload) => {
              if (ignore || !resolvedPayload) return

              setGuidance(resolvedPayload.guidance)
              if (resolvedPayload.deal) {
                onNegotiationSavedRef.current?.({ deal: resolvedPayload.deal })
              }
            })
            .catch((pollError) => {
              if (!ignore) setGuidanceError(pollError.message)
            })
            .finally(() => {
              if (!ignore && latestGuidanceRequestRef.current === requestId) {
                setIsGeneratingGuidance(false)
              }
            })
        }
      })
      .catch((loadError) => {
        if (!ignore) setError(loadError.message)
      })

    return () => {
      ignore = true
      latestGuidanceRequestRef.current += 1
    }
  }, [csrfToken, deal?.id, shouldPersist])

  useEffect(() => () => {
    const recorder = recorderRef.current
    if (recorder) {
      recorder.ondataavailable = null
      recorder.onstop = null
      if (recorder.state === 'recording') recorder.stop()
    }
    recognitionRef.current?.abort?.()
    streamRef.current?.getTracks().forEach((track) => track.stop())
    localAudioUrlsRef.current.forEach((url) => URL.revokeObjectURL(url))
  }, [])

  const handleLogUpdate = async () => {
    setSelectedOutcomeState(null)
    if (!shouldPersist) {
      await saveNegotiationNote(updateText)
      setUpdateText('')
      return
    }

    const payload = await saveNegotiationNote(updateText)
    if (payload) setUpdateText('')
  }

  const openSuggestedEvent = (payload) => {
    const suggestion = payload.suggestedNegotiationEvent
    if (!suggestion) return

    const dialog = SUGGESTED_EVENT_DIALOGS[suggestion.key]
    if (!dialog) return

    if (dialog.amountDialogType) {
      setAmountDialogType(dialog.amountDialogType)
      setCounterAmount('')
      setCounterNotes(payload.note?.content || payload.note?.body || '')
      setCounterError(null)
      return
    }

    openConfirmation(dialog.confirmationType, payload.note, payload.deal)
  }

  const openConfirmation = (type, note = null, payloadDeal = null) => {
    setPendingConfirmation({
      type,
      noteId: note?.id,
      noteMetadata: note?.metadata ?? {},
      noteContent: note?.content || note?.body,
      deal: payloadDeal,
      ...CONFIRMATION_CONTENT[type],
    })
  }

  const handleConfirmationChange = (open) => {
    if (!open && !isSavingOffer && !isSavingNote) setPendingConfirmation(null)
  }

  const confirmNegotiationEvent = async () => {
    if (!pendingConfirmation) return

    if (pendingConfirmation.type === 'vendor_rejected') {
      const payload = await createOffer({
        direction: 'received',
        status: 'rejected',
        amountCents: amountCentsFromCurrency(latestOutboundOffer),
        notes: pendingConfirmation.noteContent || 'Vendor rejected the offer',
        metadata: {
          eventOrigin: pendingConfirmation.noteId ? 'confirmed_note' : 'explicit_confirmation',
        },
        activitySource: 'manual',
      })
      if (!payload) return
    }

    if (pendingConfirmation.type === 'follow_up_scheduled') {
      const payload = await moveDealToFollowUp(pendingConfirmation.deal)
      if (!payload) return
    }

    if (pendingConfirmation.type === 'creative_openness') {
      const metadata = {
        ...pendingConfirmation.noteMetadata,
        negotiationEvent: 'vendor_open_to_creative_terms',
        negotiationEventSource: pendingConfirmation.noteId
          ? 'confirmed_transcript'
          : 'explicit_confirmation',
      }
      const payload = pendingConfirmation.noteId
        ? await confirmNoteEvent(pendingConfirmation.noteId, metadata)
        : await saveNegotiationNote('Vendor open to creative terms', {
            metadata,
            skipSuggestion: true,
          })
      if (shouldPersist && !payload) return
    }

    setPendingConfirmation(null)
    setUpdateText('')
  }

  const confirmNoteEvent = async (noteId, metadata) => {
    if (!shouldPersist) return null

    try {
      setError(null)
      setIsSavingNote(true)
      const payload = await updateDealNote(deal.id, noteId, {
        metadata,
        activitySource: 'manual',
      }, { csrfToken })
      setNotes((currentNotes) => currentNotes.map((note) => (
        note.id === noteId ? noteEntry(payload.note) : note
      )))
      markGuidanceStale()
      onNegotiationSaved?.({ deal: payload.deal })
      return payload
    } catch (saveError) {
      setError(saveError.message)
      return null
    } finally {
      setIsSavingNote(false)
    }
  }

  const moveDealToFollowUp = async (payloadDeal) => {
    if (!shouldPersist) return null

    const stagePayload = await updateDealApi(deal.id, {
      stage: 'Follow Up',
      status: 'Active',
      negotiationSummary: {
        ...(payloadDeal?.negotiationSummary ?? negotiationStatus ?? {}),
        status: 'Follow Up',
        latestOffer: latestRecordedOffer,
        walkAway,
      },
      activitySource: 'manual',
    }, { csrfToken })
    onNegotiationSaved?.({
      deal: stagePayload.deal,
      negotiationStatus: {
        ...(stagePayload.deal?.negotiationSummary ?? {}),
        status: 'Follow Up',
      },
    })
    return stagePayload
  }

  const handleCounterDialogChange = (open) => {
    if (open) return

    setAmountDialogType(null)
    setCounterAmount('')
    setCounterNotes('')
    setCounterError(null)
  }

  const handleSaveCounter = async () => {
    const amountCents = amountCentsFromCurrency(counterAmount)
    if (!amountCents || amountCents <= 0) {
      const amountError = {
        agreement: 'Enter the agreed price.',
        initial_offer: 'Enter the submitted offer amount.',
      }
      setCounterError(amountError[amountDialogType] || 'Enter the counter amount.')
      return
    }

    if (amountDialogType === 'initial_offer') {
      const notes = counterNotes.trim() || 'Initial offer submitted'
      if (!shouldPersist) {
        const amount = currencyFromCents(amountCents)
        onLogUpdate?.('Countering', amount, walkAway)
        setUpdateText('')
        handleCounterDialogChange(false)
        return
      }

      const payload = await createOffer({
        direction: 'outbound',
        status: 'submitted',
        amountCents,
        notes,
        metadata: { eventOrigin: 'confirmed_note' },
        activitySource: 'manual',
      })
      if (!payload) return

      setUpdateText('')
      handleCounterDialogChange(false)
      return
    }

    if (amountDialogType === 'agreement') {
      const agreedPrice = currencyFromCents(amountCents)
      const headsOfTermsNotes = counterNotes.trim() || 'Terms agreed'
      const payload = await createOffer({
        direction: 'received',
        status: 'accepted',
        amountCents,
        notes: headsOfTermsNotes,
        terms: {
          agreedPrice,
          headsOfTermsNotes,
        },
        metadata: { eventOrigin: 'confirmed_note' },
        activitySource: 'manual',
      })
      if (!payload) return

      onLogOutcome?.({
        stage: 'Legal / Due Diligence',
        status: 'Active',
        outcome: 'Open',
        agreedPrice,
        headsOfTermsNotes,
        latestOffer: agreedPrice,
        walkAway,
        negotiationStatus: {
          ...(payload.deal?.negotiationSummary ?? {}),
          status: 'Agreed',
          latestOffer: agreedPrice,
          walkAway,
          agreedPrice,
          headsOfTermsNotes,
        },
      })
      setUpdateText('')
      handleCounterDialogChange(false)
      return
    }

    if (!shouldPersist) {
      const amount = currencyFromCents(amountCents)
      setOffers((currentOffers) => [{
        id: `counter-${Date.now()}`,
        direction: 'received',
        status: 'countered',
        amount,
        amountCents,
        notes: counterNotes.trim() || 'Counter received',
        submittedAt: new Date().toISOString(),
        createdAt: new Date().toISOString(),
      }, ...currentOffers])
      onLogUpdate?.('Countering', amount, walkAway)
      setUpdateText('')
      handleCounterDialogChange(false)
      return
    }

    const payload = await createOffer({
      direction: 'received',
      status: 'countered',
      amountCents,
      notes: counterNotes.trim() || 'Counter received',
      metadata: { eventOrigin: 'confirmed_note' },
      activitySource: 'manual',
    })
    if (!payload) return

    setUpdateText('')
    handleCounterDialogChange(false)
  }

  const saveNegotiationNote = async (body, options = {}) => {
    if (!body.trim()) return null

    if (!shouldPersist) {
      setNotes((currentNotes) => [
        {
          id: Date.now(),
          type: 'Manual Note',
          date: 'Just now',
          content: body.trim(),
          createdAt: new Date().toISOString(),
        },
        ...currentNotes,
      ])
      return null
    }

    try {
      setError(null)
      setIsSavingNote(true)
      const payload = await createDealNote(deal.id, {
        category: 'note',
        stage: options.stage ?? 'in_negotiation',
        source: 'manual',
        body: body.trim(),
        metadata: options.metadata,
        activitySource: 'manual',
      }, { csrfToken })
      setNotes((currentNotes) => [noteEntry(payload.note), ...currentNotes])
      if (payload.suggestedNegotiationEvent && !options.skipSuggestion) openSuggestedEvent(payload)
      markGuidanceStale()
      onNegotiationSaved?.({ deal: payload.deal })
      return payload
    } catch (saveError) {
      setError(saveError.message)
      return null
    } finally {
      setIsSavingNote(false)
    }
  }

  const saveVoiceBlob = async (blob, duration, transcript) => {
    const voiceContent = transcript || 'Voice note recorded.'

    if (shouldPersist) {
      try {
        setError(null)
        setIsSavingVoice(true)
        const payload = await createDealVoiceNote(deal.id, {
          category: 'voice_note',
          stage: 'in_negotiation',
          source: 'manual',
          body: voiceContent,
          metadata: { duration },
          activitySource: 'manual',
          audio: buildVoiceFile(blob),
        }, { csrfToken })
        setNotes((currentNotes) => [noteEntry(payload.note), ...currentNotes])
        if (payload.suggestedNegotiationEvent) openSuggestedEvent(payload)
        markGuidanceStale()
        onNegotiationSaved?.({ deal: payload.deal })
      } catch (saveError) {
        setError(saveError.message)
      } finally {
        setIsRecording(false)
        setIsSavingVoice(false)
      }
      return
    }

    const audioUrl = URL.createObjectURL(blob)
    localAudioUrlsRef.current.push(audioUrl)
    setNotes((currentNotes) => [
      {
        id: Date.now(),
        type: 'Voice Note',
        date: 'Just now',
        duration,
        content: transcript,
        audioUrl,
        audioAttached: true,
        createdAt: new Date().toISOString(),
      },
      ...currentNotes,
    ])
    setIsRecording(false)
  }

  const currentTranscript = () => (
    `${finalTranscriptRef.current} ${interimTranscriptRef.current}`.trim()
  )

  const startTranscription = () => {
    const SpeechRecognition = speechRecognitionConstructor()
    if (!SpeechRecognition) {
      setTranscriptionStatus(TRANSCRIPTION_UNSUPPORTED_MESSAGE)
      return
    }

    const recognition = new SpeechRecognition()
    recognition.continuous = true
    recognition.interimResults = true
    recognition.lang = navigator.language || 'en-GB'
    recognition.onresult = (event) => {
      const { finalTranscript, interimTranscript: nextInterimTranscript } = transcriptFromResult(event)

      if (finalTranscript) {
        finalTranscriptRef.current = `${finalTranscriptRef.current} ${finalTranscript}`.trim()
      }
      interimTranscriptRef.current = nextInterimTranscript
      setTranscriptPreview(currentTranscript())
      setTranscriptionStatus(null)
    }
    recognition.onerror = () => {
      setTranscriptionStatus('Transcription paused. Audio will still be saved.')
    }

    try {
      recognition.start()
      recognitionRef.current = recognition
      setTranscriptionStatus(null)
    } catch {
      setTranscriptionStatus('Transcription could not start. Audio will still be saved.')
    }
  }

  const stopTranscription = () => {
    recognitionRef.current?.stop?.()
    recognitionRef.current = null
  }

  const handleStartRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      setError('Voice recording is not supported in this browser')
      return
    }

    try {
      setError(null)
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const mimeType = preferredAudioMimeType()
      const recorder = new window.MediaRecorder(stream, mimeType ? { mimeType } : undefined)

      streamRef.current = stream
      recorderRef.current = recorder
      audioChunksRef.current = []
      recordingStartedAtRef.current = window.performance.now()
      finalTranscriptRef.current = ''
      interimTranscriptRef.current = ''
      setTranscriptPreview('')
      setTranscriptionStatus(null)

      recorder.ondataavailable = (event) => {
        if (event.data?.size > 0) audioChunksRef.current.push(event.data)
      }

      recorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: recorder.mimeType || 'audio/webm' })
        const duration = formatDuration(window.performance.now() - recordingStartedAtRef.current)

        stream.getTracks().forEach((track) => track.stop())
        streamRef.current = null
        recorderRef.current = null

        stopTranscription()

        if (blob.size === 0) {
          setError('No audio was captured')
          setIsRecording(false)
          return
        }

        void saveVoiceBlob(blob, duration, currentTranscript())
      }

      recorder.start()
      startTranscription()
      setIsRecording(true)
    } catch (recordingError) {
      setError(recordingError.message || 'Could not start voice recording')
    }
  }

  const handleStopRecording = () => {
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop()
  }

  const handleOutcomeChange = (nextState) => {
    setSelectedOutcomeState(nextState)
  }

  const handleLogOutcome = async (updates) => {
    if (!shouldPersist) {
      onLogOutcome?.(updates)
      return
    }

    const isLostOutcome = updates.outcome === 'Lost'
    const offerStatus = isLostOutcome ? 'rejected' : 'accepted'
    const payload = await createOffer({
      direction: isLostOutcome ? 'outbound' : 'received',
      status: offerStatus,
      amountCents: amountCentsFromCurrency(updates.latestOffer),
      notes: updates.headsOfTermsNotes || updates.lostReason,
      terms: {
        agreedDate: updates.agreedDate,
        lostReason: updates.lostReason,
      },
      metadata: { eventOrigin: 'explicit_confirmation' },
      activitySource: 'manual',
    })
    if (!payload) return

    const nextNegotiationStatus = {
      ...(payload?.deal?.negotiationSummary ?? {}),
      ...(updates.negotiationStatus ?? {}),
    }
    onLogOutcome?.({
      ...updates,
      negotiationStatus: nextNegotiationStatus,
    })
  }

  const createOffer = async (data) => {
    try {
      setError(null)
      setIsSavingOffer(true)
      const payload = await createDealOffer(deal.id, data, { csrfToken })
      setOffers(payload.offers ?? [])
      markGuidanceStale()
      onNegotiationSaved?.({
        deal: payload.deal,
        negotiationStatus: payload.deal?.negotiationSummary,
      })
      return payload
    } catch (saveError) {
      setError(saveError.message)
      return null
    } finally {
      setIsSavingOffer(false)
    }
  }

  const markGuidanceStale = () => {
    setGuidance((currentGuidance) => (
      currentGuidance?.status === 'succeeded'
        ? { ...currentGuidance, stale: true }
        : currentGuidance
    ))
  }

  const handleGenerateGuidance = async () => {
    if (!shouldPersist || isGeneratingGuidance) return

    const requestId = latestGuidanceRequestRef.current + 1
    latestGuidanceRequestRef.current = requestId
    try {
      setGuidanceError(null)
      setIsGeneratingGuidance(true)
      const payload = await generateDealNegotiationGuidance(deal.id, { csrfToken })
      setGuidance(payload.guidance)
      const resolvedPayload = await pollNegotiationGuidance({
        dealId: deal.id,
        csrfToken,
        startedGuidance: payload.guidance,
        requestId,
        latestRequestRef: latestGuidanceRequestRef,
      })
      if (resolvedPayload) {
        setGuidance(resolvedPayload.guidance)
        if (resolvedPayload.deal) onNegotiationSaved?.({ deal: resolvedPayload.deal })
      }
    } catch (generationError) {
      setGuidanceError(generationError.message)
    } finally {
      if (latestGuidanceRequestRef.current === requestId) setIsGeneratingGuidance(false)
    }
  }

  return (
    <div className="mx-auto w-full max-w-[960px] pb-20">
      <NegotiationHeader statusLabel={statusLabel} />
      <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_310px]">
        <div className="min-w-0 space-y-4">
          {error ? (
            <p className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm font-semibold text-destructive">
              {error} <AiCreditUsageLink error={error} />
            </p>
          ) : null}
          <OfferStrategyStrip
            initialOffer={initialOffer}
            latestCounter={latestCounter}
            targetClose={targetClose}
            walkAway={walkAway}
          />
          <NegotiationCommandBar
            updateText={updateText}
            onUpdateTextChange={setUpdateText}
            onLogUpdate={handleLogUpdate}
            onStartRecording={handleStartRecording}
            onStopRecording={handleStopRecording}
            isRecording={isRecording}
            isSaving={isSavingOffer || isSavingNote}
            isSavingVoice={isSavingVoice}
            transcriptionText={transcriptPreview}
            transcriptionStatus={transcriptionStatus}
          />
          <NegotiationEventTimeline
            offers={offers}
            notes={notes}
          />
        </div>
        <aside
          aria-label="Negotiation guidance"
          className="w-full space-y-3 xl:sticky xl:top-6"
        >
          <ProgressionRail currentStatus={currentStatus} />
          <NegotiationGuidancePanels
            guidance={guidance}
            error={guidanceError || guidance?.errorMessage}
            isGenerating={isGeneratingGuidance}
            canGenerate={shouldPersist}
            onGenerate={handleGenerateGuidance}
            onReviewConcept={onReviewConcept}
          />
          <DealOutcomeModule
            outcomeState={outcomeState}
            latestOffer={latestRecordedOffer}
            walkAway={walkAway}
            negotiationStatus={negotiationStatus}
            isExpanded={isOutcomeExpanded}
            onToggleExpanded={() => setIsOutcomeExpanded((current) => !current)}
            onOutcomeChange={handleOutcomeChange}
            onLogOutcome={handleLogOutcome}
          />
        </aside>
      </div>
      <CounterOfferDialog
        open={Boolean(amountDialogType)}
        mode={amountDialogType}
        amount={counterAmount}
        notes={counterNotes}
        error={counterError}
        isSaving={isSavingOffer}
        onOpenChange={handleCounterDialogChange}
        onAmountChange={(value) => {
          setCounterAmount(value)
          if (counterError) setCounterError(null)
        }}
        onNotesChange={setCounterNotes}
        onSave={handleSaveCounter}
      />
      <NegotiationEventConfirmationDialog
        open={Boolean(pendingConfirmation)}
        title={pendingConfirmation?.title}
        description={pendingConfirmation?.description}
        confirmLabel={pendingConfirmation?.confirmLabel}
        isSaving={isSavingOffer || isSavingNote}
        onOpenChange={handleConfirmationChange}
        onConfirm={confirmNegotiationEvent}
      />
    </div>
  )
}

function NegotiationHeader({ statusLabel }) {
  return (
    <header
      role="banner"
      aria-label="Negotiation Engine"
      className="mb-6 flex flex-col gap-4 rounded-lg border border-border bg-card p-4 text-card-foreground shadow-sm sm:flex-row sm:items-center sm:justify-between"
    >
      <div className="flex items-center gap-3">
        <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary/10 text-primary">
          <Activity className="h-4 w-4" />
        </span>
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-sm font-semibold text-foreground">Negotiation</p>
            <span className="rounded-md bg-primary/10 px-1.5 py-0.5 text-xs font-bold uppercase tracking-wider text-primary">
              LIVE
            </span>
          </div>
          <p className="mt-1 text-sm font-semibold text-foreground">
            Execute your offer strategy and manage deal progression
          </p>
        </div>
      </div>
      <div className="inline-flex items-center gap-2 self-start rounded-md border border-border bg-background px-3 py-1.5 text-sm font-semibold text-foreground sm:self-center">
        <span className="h-2 w-2 rounded-full bg-primary" aria-hidden="true" />
        {statusLabel}
      </div>
    </header>
  )
}

function OfferStrategyStrip({ initialOffer, latestCounter, targetClose, walkAway }) {
  const offers = [
    { label: 'Initial Offer', value: initialOffer },
    { label: 'Target Close', value: targetClose, tone: 'primary' },
    { label: 'Walk Away', value: walkAway, tone: 'destructive' },
    { label: 'Latest Counter', value: latestCounter ?? NO_COUNTER_RECEIVED, tone: 'raised' },
  ]

  return (
    <section
      aria-label="Offer strategy strip"
      className="sticky top-4 z-20 rounded-lg border border-primary/30 bg-primary/10 p-3 shadow-md backdrop-blur-md"
    >
      <div className="grid gap-3 md:grid-cols-4">
        {offers.map((offer, index) => (
          <article
            key={offer.label}
            className={cn(
              'min-w-0 rounded-md px-3 py-2',
              index > 0 && 'md:border-l md:border-border',
              offer.tone === 'raised' && 'border border-border bg-card shadow-xs md:border-l-border',
            )}
          >
            <h3
              className={cn(
                'text-xs font-bold uppercase tracking-wider text-muted-foreground',
                offer.tone === 'destructive' && 'text-destructive',
                offer.tone === 'primary' && 'text-primary',
              )}
            >
              {offer.label}
            </h3>
            <p
              className={cn(
                'mt-2 text-lg font-bold tracking-normal text-foreground',
                offer.tone === 'raised' && 'text-xl',
                offer.tone === 'destructive' && 'text-destructive',
                offer.tone === 'primary' && 'text-primary',
              )}
            >
              {offer.value}
            </p>
          </article>
        ))}
      </div>
    </section>
  )
}

function NegotiationCommandBar({
  updateText,
  onUpdateTextChange,
  onLogUpdate,
  onStartRecording,
  onStopRecording,
  isRecording,
  isSaving,
  isSavingVoice,
  transcriptionText,
  transcriptionStatus,
}) {
  return (
    <section className="rounded-lg border border-border bg-card p-3 text-card-foreground shadow-sm">
      {isRecording ? (
        <div className="rounded-md border border-destructive/20 bg-destructive/10 px-4 py-3">
          <div className="flex items-center gap-4">
            <div className="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-destructive/20">
              <span className="absolute h-3 w-3 animate-ping rounded-full bg-destructive" />
              <span className="relative h-3 w-3 rounded-full bg-destructive" />
            </div>
            <div
              role="status"
              aria-live="polite"
              className="min-w-0 flex-1"
            >
              <span className="block truncate text-sm font-bold text-destructive">
                {isSavingVoice ? 'Saving Voice Note...' : 'Recording Voice Note...'}
              </span>
              <span className="block text-xs font-medium uppercase tracking-widest text-destructive/80">
                {isSavingVoice ? 'Uploading audio' : 'Listening to negotiation context'}
              </span>
            </div>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={onStopRecording}
              disabled={isSavingVoice}
            >
              <Square />
              Stop & Save
            </Button>
          </div>
          {transcriptionText || transcriptionStatus ? (
            <div className="mt-3 rounded-md border border-border bg-card/80 px-3 py-2 text-xs font-medium text-muted-foreground">
              {transcriptionText || transcriptionStatus}
            </div>
          ) : null}
        </div>
      ) : (
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end">
          <Button
            type="button"
            variant="ghost"
            aria-label="Record negotiation voice note"
            onClick={onStartRecording}
            className="h-12 w-12 shrink-0 bg-muted text-foreground hover:bg-primary/10 hover:text-primary"
          >
            <Mic className="h-4 w-4" />
          </Button>
          <Textarea
            aria-label="Log negotiation update"
            placeholder="Log update, call, or record a voice note..."
            value={updateText}
            rows={1}
            onChange={(event) => onUpdateTextChange(event.target.value)}
            className="max-h-32 min-h-12 flex-1 resize-none border-0 bg-transparent px-0 py-3 text-sm font-medium shadow-none focus-visible:border-transparent focus-visible:ring-0"
          />
          <Button
            type="button"
            className="h-12 gap-2 px-4"
            onClick={onLogUpdate}
            disabled={isSaving || !updateText.trim()}
          >
            {isSaving ? 'Saving...' : 'Log Update'} <MessageSquare className="h-4 w-4" />
          </Button>
        </div>
      )}
    </section>
  )
}

function NegotiationEventTimeline({ offers, notes }) {
  const events = [
    ...notes.map(noteTimelineEntry),
    ...offers.map(offerTimelineEntry),
  ].sort((left, right) => new Date(right.date ?? 0) - new Date(left.date ?? 0))

  if (events.length > 0) {
    return (
      <section aria-label="Negotiation timeline" className="py-1">
        <ol className="relative space-y-7 before:absolute before:bottom-0 before:left-[18px] before:top-0 before:w-px before:bg-border md:before:left-1/2">
          {events.map((event, index) => (
            <TimelineItem
              key={event.id}
              side={index % 2 === 0 ? 'right' : 'left'}
              icon={event.icon}
              tone={event.tone}
            >
              <div className="space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <span
                    className={cn(
                      'text-xs font-bold uppercase tracking-wider',
                      event.audioUrl
                        ? 'text-muted-foreground'
                        : 'rounded-md bg-primary/10 px-2 py-1 text-primary',
                    )}
                  >
                    {event.title}
                  </span>
                  <span className="text-xs font-bold text-muted-foreground">
                    {event.dateLabel || formatOfferDate(event.date)}
                  </span>
                </div>
                {event.amount ? (
                  <p className="text-2xl font-bold tracking-normal text-foreground">
                    {event.amount}
                  </p>
                ) : null}
                {event.audioUrl ? (
                  <VoiceNoteAudioPlayer
                    src={event.audioUrl}
                    duration={event.duration}
                  />
                ) : null}
                {event.notes && event.audioUrl ? (
                  <p className="line-clamp-2 text-xs font-medium italic leading-relaxed text-muted-foreground">
                    &quot;{event.notes}&quot;
                  </p>
                ) : null}
                {event.notes && !event.audioUrl ? (
                  <div className="rounded-md border border-border bg-muted/30 p-3">
                    <p className="text-sm font-semibold text-foreground">{event.notes}</p>
                  </div>
                ) : null}
                {event.audioUrl && !event.notes ? (
                  <p className="text-xs font-medium italic text-muted-foreground">
                    No transcript yet.
                  </p>
                ) : null}
                {event.duration && !event.audioUrl ? (
                  <span className="text-xs font-bold text-muted-foreground">{event.duration}</span>
                ) : null}
              </div>
            </TimelineItem>
          ))}
        </ol>
      </section>
    )
  }

  return (
    <section
      aria-label="Negotiation timeline"
      className="rounded-lg border border-dashed border-border bg-card px-6 py-10 text-center"
    >
      <History className="mx-auto h-5 w-5 text-muted-foreground" />
      <p className="mt-3 text-sm font-bold text-foreground">
        No negotiation activity logged yet.
      </p>
      <p className="mt-1 text-sm text-muted-foreground">
        Submit an offer or record a vendor response to begin the timeline.
      </p>
    </section>
  )
}

function VoiceNoteAudioPlayer({ src, duration }) {
  const audioRef = useRef(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [progress, setProgress] = useState(0)
  const [durationLabel, setDurationLabel] = useState(duration)

  const togglePlayback = () => {
    const audio = audioRef.current
    if (!audio) return

    if (isPlaying) {
      audio.pause()
      return
    }

    void audio.play().catch(() => setIsPlaying(false))
  }

  const syncProgress = () => {
    const audio = audioRef.current
    if (!audio) return

    if (Number.isFinite(audio.duration) && audio.duration > 0) {
      setProgress((audio.currentTime / audio.duration) * 100)
      setDurationLabel(formatPlaybackTime(audio.duration))
    }
  }

  const handleEnded = () => {
    setIsPlaying(false)
    setProgress(0)
  }

  const Icon = isPlaying ? Pause : Play

  return (
    <div className="flex items-center gap-3 rounded-full border border-border bg-muted/30 p-1 pr-4">
      <audio
        ref={audioRef}
        aria-label="Negotiation voice note audio"
        src={src}
        controls
        preload="metadata"
        className="sr-only"
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={handleEnded}
        onLoadedMetadata={syncProgress}
        onTimeUpdate={syncProgress}
      >
        <track kind="captions" />
      </audio>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={isPlaying ? 'Pause negotiation voice note' : 'Play negotiation voice note'}
        onClick={togglePlayback}
        className="h-8 w-8 shrink-0 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground"
      >
        <Icon className={cn('h-3.5 w-3.5', !isPlaying && 'ml-0.5 fill-current')} />
      </Button>
      <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-border">
        <span
          className={cn(
            'block h-full rounded-full bg-primary transition-all',
            progressWidthClass(progress),
          )}
        />
      </span>
      <span className="text-xs font-bold text-muted-foreground">
        {durationLabel || '0:00'}
      </span>
    </div>
  )
}

function TimelineItem({ side, icon: Icon, tone, children }) {
  return (
    <li className="relative md:grid md:grid-cols-[1fr_2.5rem_1fr] md:items-start">
      <span
        className={cn(
          'absolute left-0 top-1 z-10 flex h-9 w-9 items-center justify-center rounded-full border border-border bg-card shadow-sm md:left-1/2 md:-translate-x-1/2',
          tone === 'primary' && 'border-primary/30 bg-primary/10 text-primary',
          tone === 'muted' && 'bg-muted text-foreground',
          tone === 'neutral' && 'text-chart-4',
        )}
      >
        <Icon className="h-4 w-4" />
      </span>
      <article
        className={cn(
          'ml-12 rounded-lg border border-border bg-card p-3 text-card-foreground shadow-sm md:ml-0',
          side === 'left' && 'md:col-start-1 md:pr-3',
          side === 'right' && 'md:col-start-3 md:pl-3',
          tone === 'primary' && 'border-primary/20 bg-primary/5',
          tone === 'muted' && 'bg-card',
        )}
      >
        {children}
      </article>
    </li>
  )
}

function ProgressionRail({ currentStatus }) {
  const statusLabel = formatNegotiationStatus(currentStatus)
  const steps = [
    { label: 'Offer Made', state: 'complete' },
    { label: 'Countering', state: statusLabel === 'Countering' ? 'active' : 'complete' },
    { label: 'Agreed', state: statusLabel === 'Agreed' ? 'active' : 'pending' },
    { label: 'Legal', state: 'pending' },
  ]

  return (
    <section
      role="region"
      aria-label="Negotiation progression"
      className="rounded-lg border border-border bg-card p-4 text-card-foreground shadow-sm"
    >
      <h3 className="mb-4 text-base font-bold text-foreground">Progression</h3>
      <div className="relative grid grid-cols-4 gap-2 before:absolute before:left-6 before:right-6 before:top-4 before:h-px before:bg-border">
        {steps.map((step) => (
          <div key={step.label} className="relative z-10 flex flex-col items-center gap-2 text-center">
            <span
              className={cn(
                'flex h-8 w-8 items-center justify-center rounded-full border border-border bg-card text-muted-foreground',
                step.state === 'complete' && 'border-primary bg-primary/20 text-primary',
                step.state === 'active' && 'border-primary bg-primary text-primary-foreground',
              )}
            >
              {step.state === 'pending' ? (
                <span className="h-2 w-2 rounded-full bg-border" />
              ) : (
                <Check className="h-4 w-4" />
              )}
            </span>
            <span
              className={cn(
                'text-[11px] font-bold uppercase tracking-wider text-muted-foreground',
                step.state !== 'pending' && 'text-foreground',
                step.state === 'active' && 'text-primary',
              )}
            >
              {step.label}
            </span>
          </div>
        ))}
      </div>
    </section>
  )
}

function NegotiationGuidancePanels({
  guidance,
  error,
  isGenerating,
  canGenerate,
  onGenerate,
  onReviewConcept,
}) {
  if (isGenerating || guidanceActive(guidance)) {
    return (
      <section className="rounded-lg border border-primary/30 bg-primary/5 p-5 text-center shadow-sm">
        <Loader2 className="mx-auto h-6 w-6 animate-spin text-primary" />
        <p className="mt-3 text-sm font-bold text-foreground">Assessing negotiation position</p>
        <p className="mt-1 text-xs font-semibold text-muted-foreground">
          Aligning next move to selected strategy
        </p>
      </section>
    )
  }

  if (!guidance || guidance.status === 'failed') {
    return (
      <section className="rounded-lg border border-border bg-card p-4 text-card-foreground shadow-sm">
        <div className="flex items-center gap-2">
          <Target className="h-4 w-4 text-primary" />
          <h3 className="text-xs font-bold uppercase tracking-wider text-foreground">
            Negotiation Guidance
          </h3>
        </div>
        <p className="mt-3 text-sm font-semibold text-muted-foreground">
          No negotiation guidance generated.
        </p>
        {error ? (
          <p className="mt-2 text-sm font-semibold text-destructive">
            {error} <AiCreditUsageLink error={error} />
          </p>
        ) : null}
        <Button
          type="button"
          className="mt-4 w-full gap-2"
          disabled={!canGenerate}
          onClick={onGenerate}
        >
          <Zap className="h-4 w-4" />
          Generate Negotiation Guidance
        </Button>
      </section>
    )
  }

  return (
    <>
      {guidance.conceptReviewRequired ? (
        <section className="rounded-lg border border-chart-4/40 bg-chart-4/10 p-4 shadow-sm">
          <div className="flex items-start gap-2 text-chart-4">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <p className="text-sm font-bold">Concept judgement is out of date</p>
              <p className="mt-1 text-xs font-semibold text-muted-foreground">
                {conceptChangedEvidence(guidance).join(', ')} changed. Review before relying on the current strategy.
              </p>
            </div>
          </div>
          <Button
            type="button"
            variant="outline"
            className="mt-3 w-full"
            onClick={onReviewConcept}
          >
            Review Concept Judgement
          </Button>
        </section>
      ) : null}
      {guidance.stale ? (
        <section className="rounded-lg border border-chart-4/40 bg-chart-4/10 p-4 shadow-sm">
          <div className="flex items-start gap-2 text-chart-4">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <p className="text-sm font-bold">Guidance is out of date</p>
          </div>
          <Button type="button" variant="outline" className="mt-3 w-full gap-2" onClick={onGenerate}>
            <RefreshCw className="h-4 w-4" />
            Regenerate Guidance
          </Button>
        </section>
      ) : null}
      {error ? (
        <p className="text-sm font-semibold text-destructive">
          {error} <AiCreditUsageLink error={error} />
        </p>
      ) : null}
      <SuggestedNextMovePanel guidance={guidance} />
      <FramingScriptPanel
        script={guidance.framingScript}
        keyQuestion={guidance.keyQuestion}
      />
      <RiskSignalPanel signal={guidance.riskSignal} />
      {!guidance.stale ? (
        <Button type="button" className="w-full gap-2" onClick={onGenerate}>
          <RefreshCw className="h-4 w-4" />
          Regenerate Guidance
        </Button>
      ) : null}
    </>
  )
}

function conceptChangedEvidence(guidance) {
  const evidence = guidance?.conceptChangedEvidence
  return Array.isArray(evidence) && evidence.length > 0 ? evidence : ['Material evidence']
}

function confidenceLabel(score) {
  if (score >= 75) return 'High'
  if (score >= 50) return 'Medium'

  return 'Low'
}

function SuggestedNextMovePanel({ guidance }) {
  const confidence = Number(guidance.confidenceScore) || 0

  return (
    <section className="rounded-lg border border-primary bg-primary p-4 text-primary-foreground shadow-sm">
      <div className="mb-3 flex items-center gap-2">
        <Target className="h-4 w-4" />
        <h3 className="text-xs font-bold uppercase tracking-wider">
          Suggested Next Move
        </h3>
      </div>
      <p className="mb-3 text-xs font-bold uppercase tracking-wider opacity-80">
        Strategy: {guidance.selectedStrategyTitle}
      </p>
      {guidance.negotiationState ? (
        <p className="mb-2 text-xs font-bold uppercase tracking-wider opacity-80">
          Position: {guidance.negotiationState}
        </p>
      ) : null}
      {guidance.currentObjective ? (
        <p className="mb-3 text-sm font-semibold leading-relaxed">
          {guidance.currentObjective}
        </p>
      ) : null}
      <p className="text-lg font-bold leading-snug tracking-normal">
        {guidance.nextMove}
      </p>
      <p className="mt-3 text-sm font-medium leading-relaxed">
        {guidance.rationale}
      </p>
      <p className="mt-3 border-t border-primary-foreground/25 pt-3 text-xs font-semibold leading-relaxed">
        {guidance.strategyAlignment}
      </p>
      <div className="mt-4 flex items-center justify-between gap-3 border-t border-primary-foreground/25 pt-3">
        <span className="text-xs font-bold uppercase tracking-wider">
          Confidence Level
        </span>
        <span className="rounded-md bg-background px-2 py-1 text-sm font-bold text-primary">
          {confidenceLabel(confidence)} ({confidence}%)
        </span>
      </div>
    </section>
  )
}

function FramingScriptPanel({ script, keyQuestion }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    await navigator.clipboard.writeText(script)
    setCopied(true)
    window.setTimeout(() => setCopied(false), 2_000)
  }

  return (
    <section className="rounded-lg border border-border bg-card text-card-foreground shadow-sm">
      <div className="border-b border-border p-3">
        <h3 className="text-xs font-bold uppercase tracking-wider text-foreground">
          Framing Script
        </h3>
      </div>
      <div className="space-y-3 p-4">
        <p className="text-sm font-semibold italic leading-relaxed text-foreground">
          &quot;{script}&quot;
        </p>
        {keyQuestion ? (
          <div className="rounded-md border border-border bg-muted/40 p-3">
            <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Key Question
            </p>
            <p className="mt-1 text-sm font-semibold leading-relaxed text-foreground">
              {keyQuestion}
            </p>
          </div>
        ) : null}
        <Button
          type="button"
          variant="ghost"
          className="h-auto gap-2 px-0 text-primary"
          onClick={handleCopy}
        >
          <Clipboard className="h-4 w-4" />
          {copied ? 'Copied' : 'Copy to clipboard'}
        </Button>
      </div>
    </section>
  )
}

function RiskSignalPanel({ signal }) {
  return (
    <section className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-card-foreground shadow-sm">
      <div className="mb-3 flex items-center gap-2 text-destructive">
        <AlertTriangle className="h-4 w-4" />
        <h3 className="text-xs font-bold uppercase tracking-wider">Risk Signal</h3>
      </div>
      <p className="text-sm font-bold leading-relaxed text-foreground">
        {signal}
      </p>
    </section>
  )
}

function DealOutcomeModule({
  outcomeState,
  latestOffer,
  walkAway,
  negotiationStatus,
  isExpanded,
  onToggleExpanded,
  onOutcomeChange,
  onLogOutcome,
}) {
  return (
    <section
      role="region"
      aria-labelledby="deal-outcome-title"
      className={cn(
        'rounded-lg border bg-card text-card-foreground shadow-sm',
        outcomeState === 'agreed' && 'border-primary/30 bg-primary/5',
        outcomeState === 'lost' && 'border-destructive/30 bg-destructive/5',
        outcomeState === 'negotiating' && 'border-border',
      )}
    >
      <div className="flex items-center justify-between gap-3 p-4">
        <div className="flex items-center gap-2">
          <History className="h-4 w-4 text-foreground" />
          <h3 id="deal-outcome-title" className="text-base font-bold text-foreground">
            Deal Outcome
          </h3>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label={isExpanded ? 'Collapse deal outcome' : 'Expand deal outcome'}
          aria-expanded={isExpanded}
          aria-controls="deal-outcome-content"
          onClick={onToggleExpanded}
        >
          {isExpanded ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </Button>
      </div>

      <div
        id="deal-outcome-content"
        hidden={!isExpanded}
        className={cn(
          'space-y-4 border-t p-4',
          outcomeState === 'agreed' && 'border-primary/20',
          outcomeState === 'lost' && 'border-destructive/20',
          outcomeState === 'negotiating' && 'border-border',
        )}
      >
        <div className="grid grid-cols-3 gap-2">
          <OutcomeButton
            isSelected={outcomeState === 'negotiating'}
            icon={MessageSquare}
            label="Negotiating"
            selectedVariant="default"
            onClick={() => onOutcomeChange('negotiating')}
          />
          <OutcomeButton
            isSelected={outcomeState === 'agreed'}
            icon={Check}
            label="Agreed"
            selectedVariant="default"
            onClick={() => onOutcomeChange('agreed')}
          />
          <OutcomeButton
            isSelected={outcomeState === 'lost'}
            icon={X}
            label="Lost"
            selectedVariant="destructive"
            onClick={() => onOutcomeChange('lost')}
          />
        </div>

        {outcomeState === 'agreed' ? (
          <AgreedOutcomeForm
            latestOffer={latestOffer}
            walkAway={walkAway}
            negotiationStatus={negotiationStatus}
            onLogOutcome={onLogOutcome}
          />
        ) : null}

        {outcomeState === 'lost' ? (
          <LostOutcomeForm
            latestOffer={latestOffer}
            walkAway={walkAway}
            onLogOutcome={onLogOutcome}
          />
        ) : null}

        {outcomeState === 'negotiating' ? (
          <div className="rounded-md border border-border bg-background p-3">
            <p className="text-sm font-bold text-foreground">
              Negotiation active
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              Keep negotiation active until price or terms are resolved.
            </p>
          </div>
        ) : null}
      </div>
    </section>
  )
}

function OutcomeButton({ isSelected, icon: Icon, label, selectedVariant, onClick }) {
  return (
    <Button
      type="button"
      variant={isSelected ? selectedVariant : 'outline'}
      aria-pressed={isSelected}
      className={cn('h-auto flex-col gap-1 px-2 py-3', isSelected && 'ring-2 ring-ring')}
      onClick={onClick}
    >
      <Icon className="h-4 w-4" />
      <span>{label}</span>
    </Button>
  )
}

function AgreedOutcomeForm({ latestOffer, walkAway, negotiationStatus, onLogOutcome }) {
  const [agreedPrice, setAgreedPrice] = useState(negotiationStatus?.agreedPrice ?? latestOffer)
  const [agreedDate, setAgreedDate] = useState(negotiationStatus?.agreedDate ?? '')
  const [headsOfTermsNotes, setHeadsOfTermsNotes] = useState(negotiationStatus?.headsOfTermsNotes ?? '')

  const handleConfirmAgreement = () => {
    onLogOutcome?.({
      stage: 'Legal / Due Diligence',
      status: 'Active',
      outcome: 'Open',
      agreedPrice,
      agreedDate,
      headsOfTermsNotes,
      latestOffer: agreedPrice,
      walkAway,
      negotiationStatus: {
        status: 'Agreed',
        latestOffer: agreedPrice,
        walkAway,
        agreedPrice,
        agreedDate,
        headsOfTermsNotes,
      },
    })
  }

  return (
    <div className="space-y-4">
      <label className="block text-sm font-medium text-foreground">
        Agreed Price
        <Input
          className="mt-2 bg-background"
          value={agreedPrice}
          onChange={(event) => setAgreedPrice(event.target.value)}
        />
      </label>
      <label className="block text-sm font-medium text-foreground">
        Date Agreed
        <Input
          className="mt-2 bg-background"
          type="date"
          value={agreedDate}
          onChange={(event) => setAgreedDate(event.target.value)}
        />
      </label>
      <label className="block text-sm font-medium text-foreground">
        Heads of Terms Notes
        <Textarea
          className="mt-2 min-h-20 resize-none bg-background"
          placeholder="Key conditions..."
          value={headsOfTermsNotes}
          onChange={(event) => setHeadsOfTermsNotes(event.target.value)}
        />
      </label>
      <Button
        type="button"
        className="w-full"
        onClick={handleConfirmAgreement}
      >
        Confirm Agreement <ArrowRight className="h-4 w-4" />
      </Button>
    </div>
  )
}

function LostOutcomeForm({ latestOffer, walkAway, onLogOutcome }) {
  const [lossReason, setLossReason] = useState(DEFAULT_LOSS_REASON)

  const handleLogOutcome = () => {
    onLogOutcome?.({
      status: 'Inactive',
      outcome: 'Lost',
      isWonOrLost: 'Lost',
      latestOffer,
      walkAway,
      lostReason: lossReason,
      negotiationStatus: {
        status: 'Lost',
        latestOffer,
        walkAway,
        lossReason,
      },
    })
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <span id="loss-reason-label" className="block text-sm font-medium text-foreground">
          Reason
        </span>
        <Select value={lossReason} onValueChange={setLossReason}>
          <SelectTrigger
            aria-labelledby="loss-reason-label"
            className="w-full bg-background"
          >
            <SelectValue placeholder="Select reason" />
          </SelectTrigger>
          <SelectContent>
            {LOSS_REASON_OPTIONS.map((reason) => (
              <SelectItem key={reason} value={reason}>
                {reason}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Button
        type="button"
        variant="destructive"
        className="w-full"
        onClick={handleLogOutcome}
      >
        Log Outcome <ArrowRight className="h-4 w-4" />
      </Button>
    </div>
  )
}
